import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, firstValueFrom, forkJoin, from, map, of, switchMap, take, tap } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { Logger } from '@se/se.platform.base';
import { Html5SSOFlowType } from 'estensa-module';
import { ExtensaService } from '../connectivity/extensa.service';
import { WpfService } from '../connectivity/wpf.service';
import { CustomPageType } from '../custom-pages/cp-enum';
import { CpHttpService } from '../custom-pages/services/cp-http.service';
import { BrokerSignalStore } from '../se-broker/broker.signal.store';
import { AuthenticationSSOService, PairingDataType } from '../services/angularjs/authentication-sso.service';
import { EstensaNotificationService } from '../services/angularjs/estensa-notification.service';
import { IdleCheckerService } from '../services/idle-checker/idle-checker.service';
import { ImagingService } from '../services/imaging/imaging.service';
import { ListDataSourceService } from '../services/list-datasource.service';
import { CommonSettingsService } from '../services/settings/common-settings.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { SecondaryApplicationSettingsService } from '../services/settings/secondary-application-settings.service';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
import { SignalrNotificationsService } from '../services/signalr-notifications.service';
import { TpIntegrationsService } from '../services/tp-integrations/tp-integrations.service';
import { SsoLoginService } from '../sso/sso-login.service';
import { InMemoryTokenStore } from '../utils/token-interceptor/inmemory-tokenstore.service';
import {
    seLogin,
    seNotificationError,
    seNotificationSuccess,
    seNotificationWarning,
    seOpenImagesByEprEvent,
    seOpenViewer,
} from './root.actions';

@Injectable()
export class RootEffects {
    login$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seLogin),
                exhaustMap(({ sso }) => from(this.onLogin(sso || ''))),
            ),
        { dispatch: false },
    );

    notificationError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationError),
                tap(({ message }) => {
                    this.notificationService.error(message);
                }),
            ),
        { dispatch: false },
    );

    notificationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationSuccess),
                tap(({ message }) => {
                    this.notificationService.success(message);
                }),
            ),
        { dispatch: false },
    );

    notificationWarning$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationWarning),
                tap(({ message }) => {
                    this.notificationService.warning(message);
                }),
            ),
        { dispatch: false },
    );

    openViewerByEprEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seOpenImagesByEprEvent),
                tap(({ eprEvent, credentials, openParams }) => {
                    const sourceOpenerType = this.activeCustomPageType ?? this.activeWorklistType;
                    if (sourceOpenerType === null || sourceOpenerType === undefined) return;
                    //this.imagingService.openImagesByByEprEvent(sourceOpenerType, eprEvent, credentials, openParams);
                    /* viene chiamato il nuovo broker */
                    this.brokerStore.openImagesByEprEvent({
                        sourceOpenerType: sourceOpenerType,
                        items: eprEvent,
                        credentials: credentials,
                        openParams: openParams,
                    });
                }),
            ),
        { dispatch: false },
    );

    /* apre il viewer senza immagini */
    openViewer$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seOpenViewer),
                tap(() => {
                    const sourceOpenerType = this.activeCustomPageType ?? this.activeWorklistType;
                    let openerSource: 'generic' | 'cardio' | 'radio' = 'generic';
                    if (sourceOpenerType === CustomPageType.CARDIOLOGY_VISIT) openerSource = 'cardio';
                    else if (sourceOpenerType === CustomPageType.SPECIALTY) openerSource = 'radio';
                    this.imagingService.openViewer(openerSource);
                }),
            ),
        { dispatch: false },
    );

    brokerStore = inject(BrokerSignalStore);

    activeCustomPageType: CustomPageType | null = null;
    activeWorklistType: CustomPageType.SPECIALTY | CustomPageType.CARDIOLOGY_VISIT | null = null;

    constructor(
        private actions$: Actions,
        private authenticationSSO: AuthenticationSSOService,
        private commonSettingsService: CommonSettingsService,
        private cpHttp: CpHttpService,
        private imaging: ImagingService,
        private extensaService: ExtensaService,
        private idleChecker: IdleCheckerService,
        private logger: Logger,
        private notificationService: EstensaNotificationService,
        private radioSettingsService: RadioSettingsService,
        private secondaryApplicationSettingsService: SecondaryApplicationSettingsService,
        private signalrNotificationsService: SignalrNotificationsService,
        private ssoLoginService: SsoLoginService,
        private tokenStore: InMemoryTokenStore,
        private tpIntegrations: TpIntegrationsService,
        private userConfigurationService: UserConfigurationSettingsService,
        private wpf: WpfService,
        private router: Router,
        private imagingService: ImagingService,
        private listDataSourceService: ListDataSourceService,
    ) {
        /** 111921 patch per gestire apertura viewer dedicati da connectivity e side patient
         * dove non si ha nozione della pagina/wl in cui ci troviamo
         * e` sufficiente controllare il router, la custompage-toolbar in questo caso complica solamente il codice e non da valore aggiunto
         * per un futuro migliore bisognerebbe usare gli effects del router e modificare il root store
         * lasciando l'informazione sulla pagina correntemente attiva disponibile per tutti i componenti
         * aggiungo un @TODO... */
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((routerEvent: NavigationEnd) => {
                    switch (routerEvent.url) {
                        case '/new/vnext/cardio-visit/worklist':
                            this.activeCustomPageType = null;
                            this.activeWorklistType = CustomPageType.CARDIOLOGY_VISIT;
                            break;
                        case '/new/vnext/specialty-reporting/worklist':
                            this.activeCustomPageType = null;
                            this.activeWorklistType = CustomPageType.SPECIALTY;
                            break;
                        case '/new/vnext/ecg/worklist':
                            this.activeCustomPageType = null;
                            /* per apertura immagini da worklist trattiamo la lista ecg come cardio_visit al momento dato che non ha un settings dedicato */
                            this.activeWorklistType = CustomPageType.CARDIOLOGY_VISIT;
                            break;
                        case '/new/vnext/cardio-visit/report':
                            this.activeCustomPageType = CustomPageType.CARDIOLOGY_VISIT;
                            this.activeWorklistType = null;
                            break;
                        case '/new/vnext/specialty-reporting/report':
                            this.activeCustomPageType = CustomPageType.SPECIALTY;
                            this.activeWorklistType = null;
                            break;
                        default: {
                            this.activeCustomPageType = null;
                            this.activeWorklistType = null;
                        }
                    }
                }),
            )
            .subscribe();

        // BROKER CHECK !!!!!
        /*
        By calling these methods from root.effects, the application can ensure that the necessary data is loaded and processed in the correct order,
        and that any resulting UI updates are handled properly. Could be used on each single component when these components switch to STANDALONE mode
        */
        // Load exams from the broker store using the current broker parameters 📊
        this.brokerStore.loadExams(this.brokerStore.brokerParams);
        // Load studies from exams using the loaded exams 📚
        this.brokerStore.loadStudiesFromExams(this.brokerStore.exams);
        // Check studies to open a popup using the loaded studies 📣
        this.brokerStore.onCheckStudiesToOpenPopUp(this.brokerStore.studies);
        // BROKER CHECK !!!!!
    }

    private async onLogin(sso: string): Promise<void | any[]> {
        try {
            // Servizi core
            const [_, radioSettings] = await Promise.all([
                this.userConfigurationService.initService(),
                this.radioSettingsService.initService(),
                this.secondaryApplicationSettingsService.initService(),
                this.commonSettingsService.initService(),
            ]);
            await this.extensaService.init();
            this.signalrNotificationsService.init();
            this.listDataSourceService.preloadLists();
            // Servizi dipendenti da connettività
            await Promise.all([this.imaging.initService(), this.wpf.initService()]);

            //Connessione a WPF
            if (sso === 'WPF') {
                await this.extensaService.startConnection(this.tokenStore.getToken());
            }

            if (radioSettings.idleCheckSettings?.enabled) {
                this.idleChecker.enable(radioSettings.idleCheckSettings);
            }

            if (radioSettings.html5ConfigSettings.html5SsoSettings.flowType !== Html5SSOFlowType.Disabled) {
                await this.handleSSOLogin();
            }

            this.preloadCustomPageItemSources();

            await this.signalrNotificationsService.getEcgActiveAlarms(true);
        } catch (err: any) {
            this.logger.error(err.message);
        }
    }

    private async handleSSOLogin() {
        const { pt } = this.authenticationSSO.getQueryString();
        if (!pt) {
            return;
        }

        const data = await firstValueFrom(this.authenticationSSO.getPairingData(pt));
        if (
            !data ||
            data.dataType !== PairingDataType.ParameterDictionary ||
            !data.parameterDictionary ||
            !data.parameterDictionary.accessTokenResponse
        ) {
            return;
        }
        const accessTokenResponse = JSON.parse(data.parameterDictionary.accessTokenResponse);

        this.ssoLoginService.setOpenIdToken(accessTokenResponse.id_token);
        this.tpIntegrations.isActionReady$
            .pipe(
                filter((isReady) => isReady),
                take(1),
            )
            .subscribe(() => {
                this.tpIntegrations.onCallAction({
                    action: 'REFRESH_TOKEN',
                    data: {
                        actionId: 'test-token',
                        refreshUri: 'client/api/sso/refresh',
                        cacheKey: 'sso-token',
                        cacheInitialValue: accessTokenResponse,
                        isReset: false,
                    },
                });
            });
    }

    private preloadCustomPageItemSources() {
        // Utilizziamo la chiamata solo per popolare la cache.
        // Non facciamo await perché vogliamo che succeda in background.
        this.cpHttp
            .getPreloadableLayouts()
            .pipe(
                switchMap((layouts) =>
                    forkJoin(
                        layouts
                            .filter((layout) => layout.isActive)
                            .sort((a, b) => b.order - a.order)
                            .slice(0, 3)
                            .map((l) =>
                                this.cpHttp
                                    .getItemSourcesWithItemsByIdCustomPage(l.id, true)
                                    .pipe(catchError(() => of(undefined))),
                            ),
                    ),
                ),
            )
            .subscribe();
    }
}
